import { z } from 'zod';

export const OptionsSchema = z.object({
  isDefault: z.boolean().optional(),
  fonts: z
    .object({
      primaryFontUrl: z.string(),
      boldFontUrl: z.string(),
      lightFontUrl: z.string().optional(),
      alwaysUsePartnerFont: z.boolean().optional(),
      defaultFontUrl: z.string().optional(),
    })
    .optional(),
  images: z.object({
    loadingImage: z.string().optional(),
    heroBanner: z.string().optional(),
    localizedHeroBanner: z.record(z.string()).optional(),
    unexpectedErrorIcon: z.string().optional(),
    sessionTimeoutIcon: z.string().optional(),
    macroArticleIcon: z.string().optional(),
    tooltip: z.string().optional(),
    attachmentIconPath: z.string().optional(),
    typingIndicatorAvatar: z
      .object({
        url: z.string(),
        alt: z.string().optional(),
        lottieUrl: z.string(),
      })
      .optional(),
    salesDisclosureHero: z.string().optional(),
    voipSidePanelIcon: z
      .object({
        url: z.string(),
        alt: z.string().optional(),
        lottieUrl: z.string().optional(),
      })
      .optional(),
    asurionExpertLogo: z.string().optional(),
  }),
  colors: z
    .object({
      primary: z.string().optional(),
      secondary: z.string().optional(),
      background: z
        .object({
          main: z.string(),
          light: z.string(),
          accent: z.string(),
          header: z.string(),
          disabled: z.string().optional(),
          element: z.string().optional(),
          startOverButton: z.string().optional(),
          tonal: z.string().optional(),
        })
        .optional(),
      hoverBackground: z
        .object({
          primaryHover: z.string(),
          secondaryHover: z.string(),
          accentHover: z.string(),
        })
        .optional(),
      messageBackgrounds: z
        .object({
          outbound: z.string(),
          inbound: z.string(),
        })
        .optional(),
      text: z
        .object({
          primaryLight: z.string(),
          secondaryLight: z.string(),
          primaryDark: z.string(),
          secondaryDark: z.string(),
          disabled: z.string(),
          buttonRegular: z.string(),
          buttonHover: z.string(),
          error: z.string(),
          qr: z.string(),
          tonal: z.string().optional(),
        })
        .optional(),
      border: z
        .object({
          default: z.string(),
          dividerOnDark: z.string(),
          active: z.string(),
          error: z.string(),
          disabled: z.string(),
        })
        .optional(),
      controls: z
        .object({
          uncheckedControl: z.string(),
          inboundChecked: z.string(),
          outboundChecked: z.string(),
          disabledControl: z.string(),
        })
        .optional(),
      launcher: z
        .object({
          background: z.string().optional(),
          hoverBackground: z.string().optional(),
        })
        .optional(),
      scrollToBottom: z
        .object({
          background: z.string().optional(),
          hoverBackground: z.string().optional(),
        })
        .optional(),
      link: z
        .object({
          inboundDefaultLink: z.string(),
          inboundDisabledLink: z.string(),
          inboundHoverLink: z.string(),
          outboundDefaultLink: z.string(),
          outboundDisabledLink: z.string(),
          outboundHoverLink: z.string(),
        })
        .optional(),
      accent: z
        .object({
          preTransitAccent: z.string(),
          inTransitAccent: z.string(),
          successAccent: z.string(),
          noInfoAccent: z.string(),
        })
        .optional(),
      system: z
        .object({
          errorIcon: z.string(),
          errorHoverIcon: z.string(),
        })
        .optional(),
      boxShadow: z.string().optional(),
      ratingIcon: z
        .object({
          INBOUND: z.string(),
          OUTBOUND: z.string(),
        })
        .optional(),
    })
    .optional(),
  launcher: z
    .object({
      initializeFromLauncher: z.boolean().optional(),
      imageSrc: z.string(),
      backgroundColor: z.string(),
    })
    .optional(),
  text: z.object({
    partnerFriendlyName: z.string().optional(),
    uppercaseButtonText: z.boolean().optional(),
    routeToAgentMessage: z.string().optional(),
    addToCartKey: z.string().optional(),
    productUnavailableKey: z.string().optional(),
    poweredByEntity: z.string().optional(),
    headerTitle: z.string().optional(),
  }),
  mainWidget: z
    .object({
      position: z.any(),
      boxShadow: z.string().optional(),
      buttonVariant: z.string().optional(),
      buttonSize: z.string().optional(),
      hideHeader: z.boolean().optional(),
    })
    .optional(),
  privacyPopup: z
    .object({
      img: z.string(),
      title: z.string(),
      text: z.string(),
      url: z.string(),
    })
    .optional(),
  analytics: z
    .object({
      partnerId: z.string().optional(),
    })
    .optional(),
  experimental: z
    .object({
      imageFit: z.string().optional(),
      hideMessageStamps: z.boolean().optional(),
      showAttachmentButton: z.boolean().optional(),
      enableLocalization: z.boolean().optional(),
      hideHeaderSubtitle: z.boolean().optional(),
      mlContextualDialog: z
        .object({
          message: z.string().optional(),
          options: z.any().optional(),
          reanimate: z.number().optional(),
        })
        .optional(),
      enableLongMessageSplitting: z.boolean().optional(),
      enableTroubleshootingImages: z.boolean().optional(),
      enableMsgAnchorTop: z.boolean().optional(),
      surveyFlowId: z.string().optional(),
      urlsForSidePanel: z.array(z.string()).optional(),
      enableTooltip: z
        .object({
          urlsThatAlwaysShowTooltip: z.array(z.string()),
        })
        .optional(),
      callbackPopup: z
        .object({
          bannerImageUrl: z.string().optional(),
          headerTitle: z.string().optional(),
          rating: z.number().optional(),
        })
        .optional(),
      thumbsComponent: z
        .object({
          enabled: z.boolean(),
          feedbackQuestion: z.string(),
          successMessage: z.string(),
          options: z.array(
            z.object({
              tooltip: z.string(),
              value: z.string(),
              icon: z.string(),
            })
          ),
        })
        .optional(),
      hideSelectSubscriptionTitleAndPrice: z.boolean().optional(),
    })
    .optional(),
});

export type Options = z.infer<typeof OptionsSchema>;
